import { useMemo } from 'react';
import {
  Skeleton as AntSkeleton,
  SkeletonProps as AntSkeletonProps,
  theme,
} from 'antd';
import type { GlobalToken } from 'antd';
import styled, { keyframes } from 'styled-components';
import { radius } from '../../theme/radius';

export type SkeletonVariant = 'text' | 'circular' | 'rectangular' | 'card';

export interface SkeletonProps extends Omit<
  AntSkeletonProps,
  'avatar' | 'title' | 'paragraph'
> {
  /** Shape variant of the skeleton */
  variant?: SkeletonVariant;
  /** Width of the skeleton */
  width?: number | string;
  /** Height of the skeleton */
  height?: number | string;
  /** Number of text rows (only for variant="text") */
  rows?: number;
  /** Whether to show animation */
  animated?: boolean;
  /** Border radius override */
  borderRadius?: number | string;
}

interface TokenProps {
  $token: GlobalToken;
}

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const SkeletonBase = styled.div<
  TokenProps & {
    $width?: number | string;
    $height?: number | string;
    $borderRadius?: number | string;
    $animated?: boolean;
  }
>`
  background: linear-gradient(
    90deg,
    ${({ $token }) => $token.colorFillSecondary} 25%,
    ${({ $token }) => $token.colorFillTertiary} 50%,
    ${({ $token }) => $token.colorFillSecondary} 75%
  );
  background-size: 200% 100%;
  animation: ${({ $animated }) => ($animated ? shimmer : 'none')} 1.5s
    ease-in-out infinite;
  width: ${({ $width }) =>
    typeof $width === 'number' ? `${$width}px` : $width || '100%'};
  height: ${({ $height }) =>
    typeof $height === 'number' ? `${$height}px` : $height || '16px'};
  border-radius: ${({ $borderRadius, $token }) =>
    typeof $borderRadius === 'number'
      ? `${$borderRadius}px`
      : $borderRadius || `${$token.borderRadiusSM}px`};
`;

const getCircularHeight = (
  height: number | string | undefined,
  width: number | string | undefined
): string => {
  if (typeof height === 'number') return `${height}px`;
  if (height) return height;
  if (typeof width === 'number') return `${width}px`;
  if (width) return width;
  return '40px';
};

const CircularSkeleton = styled(SkeletonBase)`
  border-radius: ${radius.circle};
  width: ${({ $width }) =>
    typeof $width === 'number' ? `${$width}px` : $width || '40px'};
  height: ${({ $height, $width }) => getCircularHeight($height, $width)};
`;

const CardSkeleton = styled.div<TokenProps>`
  background: ${({ $token }) => $token.colorBgContainer};
  border-radius: ${radius.box}px;
  padding: ${({ $token }) => $token.padding}px;
  box-shadow: ${({ $token }) => $token.boxShadowTertiary};
  border: ${({ $token }) => $token.lineWidth}px solid
    ${({ $token }) => $token.colorBorderSecondary};
`;

const CardSkeletonHeader = styled.div<TokenProps>`
  display: flex;
  align-items: center;
  gap: ${({ $token }) => $token.marginSM}px;
  margin-bottom: ${({ $token }) => $token.margin}px;
`;

const CardSkeletonContent = styled.div<TokenProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ $token }) => $token.marginXS}px;
`;

const TextRowsContainer = styled.div<TokenProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ $token }) => $token.marginXS}px;
  width: 100%;
`;

const TextRow = styled(SkeletonBase)<{ $lastRow?: boolean }>`
  width: ${({ $lastRow }) => ($lastRow ? '60%' : '100%')};
  height: 16px;
`;

const StyledAntSkeleton = styled(AntSkeleton)<TokenProps>`
  .ant-skeleton-content {
    .ant-skeleton-title {
      background: linear-gradient(
        90deg,
        ${({ $token }) => $token.colorFillSecondary} 25%,
        ${({ $token }) => $token.colorFillTertiary} 50%,
        ${({ $token }) => $token.colorFillSecondary} 75%
      );
      background-size: 200% 100%;
      border-radius: ${({ $token }) => $token.borderRadiusSM}px;
    }

    .ant-skeleton-paragraph > li {
      background: linear-gradient(
        90deg,
        ${({ $token }) => $token.colorFillSecondary} 25%,
        ${({ $token }) => $token.colorFillTertiary} 50%,
        ${({ $token }) => $token.colorFillSecondary} 75%
      );
      background-size: 200% 100%;
      border-radius: ${({ $token }) => $token.borderRadiusSM}px;
    }
  }

  .ant-skeleton-avatar {
    background: linear-gradient(
      90deg,
      ${({ $token }) => $token.colorFillSecondary} 25%,
      ${({ $token }) => $token.colorFillTertiary} 50%,
      ${({ $token }) => $token.colorFillSecondary} 75%
    );
    background-size: 200% 100%;
  }
`;

/**
 * Skeleton component based on Libertify Design System
 *
 * Used to display a placeholder while content is loading.
 *
 * @example
 * ```tsx
 * import { Skeleton } from '@libertify/ui';
 *
 * // Text skeleton
 * <Skeleton variant="text" rows={3} />
 *
 * // Circular skeleton (for avatars)
 * <Skeleton variant="circular" width={48} height={48} />
 *
 * // Rectangular skeleton
 * <Skeleton variant="rectangular" width={200} height={100} />
 *
 * // Card skeleton
 * <Skeleton variant="card" />
 * ```
 */
export const Skeleton = ({
  variant = 'text',
  width,
  height,
  rows = 3,
  animated = true,
  borderRadius,
  ...props
}: SkeletonProps) => {
  const { token } = theme.useToken();
  const rowIds = useMemo(
    () => Array.from({ length: rows }, (_, i) => `skeleton-row-${i}-${rows}`),
    [rows]
  );

  if (variant === 'circular') {
    return (
      <CircularSkeleton
        $token={token}
        $width={width}
        $height={height}
        $animated={animated}
        $borderRadius={borderRadius}
      />
    );
  }

  if (variant === 'rectangular') {
    return (
      <SkeletonBase
        $token={token}
        $width={width}
        $height={height || 100}
        $animated={animated}
        $borderRadius={borderRadius || 8}
      />
    );
  }

  if (variant === 'card') {
    return (
      <CardSkeleton $token={token}>
        <CardSkeletonHeader $token={token}>
          <CircularSkeleton
            $token={token}
            $width={40}
            $height={40}
            $animated={animated}
          />
          <div style={{ flex: 1 }}>
            <SkeletonBase
              $token={token}
              $width="60%"
              $height={16}
              $animated={animated}
              style={{ marginBottom: 8 }}
            />
            <SkeletonBase
              $token={token}
              $width="40%"
              $height={12}
              $animated={animated}
            />
          </div>
        </CardSkeletonHeader>
        <CardSkeletonContent $token={token}>
          <SkeletonBase
            $token={token}
            $width="100%"
            $height={12}
            $animated={animated}
          />
          <SkeletonBase
            $token={token}
            $width="100%"
            $height={12}
            $animated={animated}
          />
          <SkeletonBase
            $token={token}
            $width="70%"
            $height={12}
            $animated={animated}
          />
        </CardSkeletonContent>
      </CardSkeleton>
    );
  }

  // Text variant
  if (rows > 0) {
    return (
      <TextRowsContainer $token={token}>
        {rowIds.map((id, index) => (
          <TextRow
            key={id}
            $token={token}
            $animated={animated}
            $lastRow={index === rows - 1}
            $width={width}
            $height={height}
          />
        ))}
      </TextRowsContainer>
    );
  }

  // Fallback to Ant Design Skeleton for complex cases
  return <StyledAntSkeleton $token={token} active={animated} {...props} />;
};

export default Skeleton;
